import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Multiselect } from "react-widgets";

import { xml2js } from "../../json/xml2js";
import bintiOptions from "../../options/binti.json";
import {
  deleteFormSubmission,
  getConfig,
  getFieldValue,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import { ArrayCheck } from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import Comment from "../common/Comment";
import { Input, Select } from "../common/ControlBundle";
import ShareModal from "../common/ShareModal";
import { getMergedHomes } from "../services/soar";
import { getSchema } from "./joi/validation";

function BulkCaseNote() {
  const navigate = useNavigate();

  //Get route and query string params
  const location = useLocation();
  const { formid, submissionid } = useParams();
  const qs = queryString.parse(location);
  console.log("Params", formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadStatus, setLoadStatus] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid); // Latest Form Version Id
  console.log("Schema Config: ", schema);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  //Get Client Info
  useEffect(() => {
    if (!submissionid) {
      return;
    }

    const loadSubmission = async () => {
      const response = await getSubmission(submissionid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setData(response.Data);
      setFormsyDetails(response.FormsyDetails);

      if (response.Comments) {
        setComments(response.Comments);
      }

      if (response.Signatures) {
        setSignatures(response.Signatures);
      }
      setLoadStatus((ls) => ({ ...ls, dataSet: true }));
    };

    loadSubmission();
  }, [submissionid]);

  useEffect(() => {
    if (
      Object.keys(formsyConfig).length > 0 ||
      loadStatus.loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => ({ ...ls, loadingConfig: true }));
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: "",
          ContactName: "",
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => ({ ...ls, loadingConfig: false, dataSet: true }));

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    formid,
    loadStatus.loadingConfig,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  //Form validation
  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (!dataObj.SelectedState.value && userBusinessUnit !== "ccplive") {
      dataObj.SelectedState.value = userBusinessUnit
        .replace("SAFY of ", "")
        .replace(" ", "")
        .toLowerCase();
      setData(dataObj);
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////SHARE
  // const toggleShareModal = () => {
  //   setShowShare(!showShare);
  // };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property].label !== undefined && data[property].label !== ""
              ? data[property].label
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //CUSTOM
  const [mergedHomes, setMergedHomes] = useState([]);
  const [homesLoadStatus, setHomesLoadStatus] = useState({});

  useEffect(() => {
    if (
      !loadStatus.dataSet ||
      data.UserBintiId?.value ||
      !safyInfo?.user?.safyInfo?.BusinessUnitIdName ||
      !safyInfo?.user?.safyInfo?.binti?.find
    ) {
      return;
    }

    let userState;
    if (safyInfo.user.safyInfo.BusinessUnitIdName === "ccplive") {
      if (!data.SelectedState?.value) {
        return;
      }

      userState = data.SelectedState.value;
    } else {
      userState = safyInfo.user.safyInfo.BusinessUnitIdName.replace(
        "SAFY of ",
        ""
      )
        .replace(" ", "")
        .toLowerCase();
    }

    const bintiState = safyInfo.user.safyInfo.binti.find(
      (b) => b.company === userState
    );

    if (!bintiState) {
      return;
    }

    setData((d) => {
      const obj = { ...d };
      obj.UserBintiId.value = bintiState.id;
      return obj;
    });
  }, [
    loadStatus.dataSet,
    data.UserBintiId?.value,
    data.SelectedState?.value,
    safyInfo,
  ]);

  const userBusinessUnit = useMemo(() => {
    if (!safyInfo?.user?.safyInfo?.BusinessUnitIdName) {
      return;
    }

    return safyInfo.user.safyInfo.BusinessUnitIdName;
  }, [safyInfo]);

  const userIsAdmin = useMemo(() => {
    if (!safyInfo?.user?.modules) {
      return false;
    }

    return safyInfo.user.modules.includes("Admin");
  }, [safyInfo]);

  const recipientOptions = useMemo(() => {
    const options = mergedHomes.map((app) => {
      const primaryContactId = app.HomeData[0].members.filter(
        (mem) => mem.primarycontact === "Yes"
      )[0];
      if (!primaryContactId) {
        return [];
      }

      return {
        _id: primaryContactId.contactid,
        name: app.HomeData[0].fostercarehomename,
      };
    });

    return options;
  }, [mergedHomes]);

  const loadMergedHomes = useCallback(async (state) => {
    setHomesLoadStatus({ loading: true });
    const toastId = toast.loading("Loading Binti Applicants...", {
      type: toast.TYPE.INFO,
    });
    const response = await getMergedHomes(state);

    if (!isMountedRef.current) {
      toast.dismiss(toastId);
      return;
    }

    setMergedHomes(response ? JSON.parse(response) : []);
    setHomesLoadStatus({ loaded: true });
    toast.update(toastId, {
      render: "Binti Applicants loaded.",
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 5000,
      closeOnClick: true,
    });
  }, []);

  useEffect(() => {
    const localSafyInfo = JSON.parse(localStorage.getItem("safyInfo") || "{}");
    const businessUnit = localSafyInfo.user?.safyInfo?.BusinessUnitIdName;

    if (homesLoadStatus.loaded || homesLoadStatus.loading || !businessUnit) {
      return;
    }

    if (businessUnit === "ccplive") {
      if (data.SelectedState?.value) {
        loadMergedHomes(data.SelectedState.value);
      }
    } else {
      const userState = businessUnit
        .replace("SAFY of ", "")
        .replace(" ", "")
        .toLowerCase();
      loadMergedHomes(userState);
    }
  }, [homesLoadStatus, data.SelectedState?.value, loadMergedHomes]);

  const handleSelectedRecipientsChange = (items) => {
    const e = {
      currentTarget: {
        name: "SelectedRecipients",
        value: JSON.stringify(items.map((item) => item._id)),
        type: "multiselect",
      },
    };
    handleChange(e);
  };

  const handleSelectedStateChange = async ({ currentTarget: input }) => {
    const obj = { ...data };
    obj.SelectedState.value = input.value;
    setData(obj);

    if (!input.value) {
      return;
    }

    loadMergedHomes(input.value);
  };

  const needsContactMethod = useMemo(() => {
    if (!data.Type) {
      return false;
    }

    const value = getFieldValue(data.Type);

    return (
      value === "case_update" ||
      value === "next_planned_contact" ||
      value === "other" ||
      value === "recruiting" ||
      value === "family_evaluation"
    );
  }, [data]);

  const validate = () => {
    const results = validateForm(data, schema);

    if (getFieldValue(data.Type) === "next_planned_contact") {
      if (results.Date) {
        setErrors(results);
        return results;
      }

      const now = moment().startOf("day");
      const nextDate = moment(getFieldValue(data.Date));
      if (nextDate.isBefore(now)) {
        const errs = { ...results };
        errs.Date = "Date cannot be in the past.";
        setErrors(errs);
        return errs;
      }
    }

    setErrors(results);
    return results;
  };

  const markFinalSigned = () => {
    if (Object.keys(validate()).length > 0) {
      return;
    }

    const details = { ...formsyDetails };
    details.FinalSigned = "1";
    details.FinalSignedDate = new Date();
    setFormsyDetails(details);
    saveForm(data, details, comments, signatures, navigate, location);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    deleteFormSubmission(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location
    );
  };

  const getSelectedRecipients = (value) => {
    return value ? JSON.parse(value) : [];
  };

  const processErrors = useMemo(() => {
    if (!data.ProcessErrors?.value) {
      return [];
    }

    return JSON.parse(data.ProcessErrors.value);
  }, [data.ProcessErrors?.value]);

  const successfullyProcessed = useMemo(() => {
    if (!data.BulkProcessed?.value) {
      return [];
    }

    return JSON.parse(data.BulkProcessed.value);
  }, [data.BulkProcessed?.value]);

  useEffect(() => {
    if (!data.Body?.value) {
      return;
    }

    setData((d) => {
      const replaced = d.Body.value.replaceAll('"', "'");

      if (d.Body.value === replaced) {
        return d;
      }

      const obj = { ...d };
      obj.Body.value = replaced;
      return obj;
    });
  }, [data.Body?.value]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              {formsyDetails.CreatedById?.localeCompare(
                safyInfo?.user.azureId,
                undefined,
                { sensitivity: "accent" }
              ) !== 0 ? (
                formsyDetails.FinalSigned === "1" ? (
                  <div className="p-2 mb-2 bg-warning rounded">
                    This Bulk Case Note has been submitted.
                  </div>
                ) : (
                  <div className="p-2 mb-2 bg-info text-white rounded">
                    This Bulk Case Note is open/has not been submitted.
                  </div>
                )
              ) : null}
              {userBusinessUnit === "ccplive" || userIsAdmin ? (
                <Select
                  name="SelectedState"
                  label={data.SelectedState.label}
                  error={errors.SelectedState}
                  value={getFieldValue(data.SelectedState)}
                  options={bintiOptions.States}
                  onChange={handleSelectedStateChange}
                  disabled={
                    formDisabled ||
                    (!userIsAdmin &&
                      formsyDetails.CreatedById?.localeCompare(
                        safyInfo?.user.azureId,
                        undefined,
                        { sensitivity: "accent" }
                      ) !== 0)
                  }
                />
              ) : null}
              {!(userBusinessUnit === "ccplive" || userIsAdmin) ||
              data.SelectedState.value ? (
                <>
                  <div className="row">
                    <div className="col-8">
                      <label htmlFor="SelectedRecipients">
                        {data.SelectedRecipients.label}
                        {processErrors.length > 0 ? (
                          <>
                            <br />
                            <span>
                              Errors shaded in{" "}
                              <span className="text-danger">RED</span>
                            </span>
                          </>
                        ) : null}
                      </label>
                      <Multiselect
                        name="SelectedRecipients"
                        data={recipientOptions}
                        dataKey="_id"
                        textField="name"
                        value={getSelectedRecipients(
                          data.SelectedRecipients.value
                        )}
                        onChange={handleSelectedRecipientsChange}
                        className="mt-2"
                        disabled={
                          formDisabled ||
                          formsyDetails.CreatedById?.localeCompare(
                            safyInfo?.user.azureId,
                            undefined,
                            { sensitivity: "accent" }
                          ) !== 0
                        }
                        renderTagValue={({ item }) => (
                          <span
                            className={
                              processErrors.includes(item._id)
                                ? "fw-bold text-danger"
                                : successfullyProcessed.includes(item._id)
                                ? "text-success"
                                : ""
                            }
                          >
                            {item.name}
                          </span>
                        )}
                      />
                    </div>
                    <div className="col">
                      <Input
                        label="# of Recipients Selected:"
                        value={
                          getSelectedRecipients(data.SelectedRecipients.value)
                            .length
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="Type"
                        label={data.Type.label}
                        error={errors.Type}
                        value={getFieldValue(data.Type)}
                        options={bintiOptions.ContactTypes}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          formsyDetails.CreatedById?.localeCompare(
                            safyInfo?.user.azureId,
                            undefined,
                            { sensitivity: "accent" }
                          ) !== 0
                        }
                      />
                    </div>
                    <div className="col-3">
                      <Input
                        name="Date"
                        label={
                          getFieldValue(data.Type) === "next_planned_contact"
                            ? "Future Date"
                            : data.Date.label
                        }
                        error={errors.Date}
                        value={getFieldValue(data.Date)}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          formsyDetails.CreatedById?.localeCompare(
                            safyInfo?.user.azureId,
                            undefined,
                            { sensitivity: "accent" }
                          ) !== 0
                        }
                        type="date"
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="Subject"
                        label={data.Subject.label}
                        error={errors.Subject}
                        value={getFieldValue(data.Subject)}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={
                          formDisabled ||
                          formsyDetails.CreatedById?.localeCompare(
                            safyInfo?.user.azureId,
                            undefined,
                            { sensitivity: "accent" }
                          ) !== 0
                        }
                      />
                    </div>
                  </div>
                  <div className="row">
                    {needsContactMethod ? (
                      <div className="col-4">
                        <Select
                          name="ContactMethod"
                          label={data.ContactMethod.label}
                          error={errors.ContactMethod}
                          value={getFieldValue(data.ContactMethod)}
                          options={bintiOptions.ContactMethods}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled ||
                            formsyDetails.CreatedById?.localeCompare(
                              safyInfo?.user.azureId,
                              undefined,
                              { sensitivity: "accent" }
                            ) !== 0
                          }
                        />
                      </div>
                    ) : null}
                    {getFieldValue(data.Type) === "approval_date_estimate" ? (
                      <div className="col-4">
                        <Input
                          name="EstimatedApprovalDate"
                          label={data.EstimatedApprovalDate.label}
                          error={errors.EstimatedApprovalDate}
                          value={getFieldValue(data.EstimatedApprovalDate)}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={
                            formDisabled ||
                            formsyDetails.CreatedById?.localeCompare(
                              safyInfo?.user.azureId,
                              undefined,
                              { sensitivity: "accent" }
                            ) !== 0
                          }
                          type="date"
                        />
                      </div>
                    ) : null}
                  </div>
                  <Input
                    name="Body"
                    label={data.Body.label}
                    error={errors.Body}
                    value={getFieldValue(data.Body)}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={
                      formDisabled ||
                      formsyDetails.CreatedById?.localeCompare(
                        safyInfo?.user.azureId,
                        undefined,
                        { sensitivity: "accent" }
                      ) !== 0
                    }
                  />
                </>
              ) : null}
            </>
          )}
          <div className="clearfix">
            <div className="float-end">
              {submissionid &&
              !formDisabled &&
              (userIsAdmin ||
                formsyDetails.CreatedById?.localeCompare(
                  safyInfo?.user.azureId,
                  undefined,
                  { sensitivity: "accent" }
                ) === 0) ? (
                <div className="btn m-2 d-print-none" onClick={handleDelete}>
                  <i className="fa-solid fa-trash-can fa-xl"></i>
                </div>
              ) : null}
              <div
                className="btn btn-danger m-2 d-print-none"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </div>
              {/* {!formDisabled &&
              (!submissionid ||
                userIsAdmin ||
                formsyDetails.CreatedById?.localeCompare(
                  safyInfo?.user.azureId,
                  undefined,
                  { sensitivity: "accent" }
                ) === 0) ? ( */}
              <div
                className="btn btn-primary m-2 d-print-none"
                onClick={() => {
                  handleSubmit(null, null, null, null);
                }}
              >
                Save
              </div>
              {/* ) : null} */}
              {!formDisabled &&
              formsyDetails.CreatedById?.localeCompare(
                safyInfo?.user.azureId,
                undefined,
                { sensitivity: "accent" }
              ) === 0 ? (
                <div
                  className="btn btn-success m-2 d-print-none"
                  onClick={markFinalSigned}
                >
                  Submit
                </div>
              ) : null}
            </div>
          </div>
          <Comment
            showModal={showComment}
            commentToEdit={commentToEdit}
            onHide={() => {
              setShowComment(false);
              setCommentToEdit(undefined);
            }}
            onSave={onCommentSave}
            fields={commentFields()}
          />
          {showShare ? (
            <ShareModal
              showShare={showShare}
              onHide={() => setShowShare(false)}
              onSend={handleShareSend}
              submissionId={submissionid}
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

export default BulkCaseNote;
